//@flow

import React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { FormattedMessage, FormattedDate } from 'react-intl';
import getter from 'dibs-object-getter';
import classnames from 'classnames';
import CheckmarkIcon from 'dibs-vg/dist/react/checkmark';
import LockIcon from 'dibs-vg/dist/react/lock-open-filled';
import { BarHeader } from 'dibs-elements/exports/BarHeader';
import {
    TPN_TIER_MEMBER,
    TPN_TIER_BRONZE,
    TPN_TIER_SILVER,
    TPN_TIER_GOLD,
    TPN_TIER_PLATINUM,
} from 'dibs-trade-rewards-progress/exports/tpnStatusCodes';

import { ProfileShowcase } from './ProfileShowcase';

import styles from './NextStatusComparison.scss';

import type { Node } from 'react';
import type { NextStatusComparison_tradeFirm as TradeFirm } from './__generated__/NextStatusComparison_tradeFirm.graphql';
import type { NextStatusComparison_viewer as Viewer } from './__generated__/NextStatusComparison_viewer.graphql';

const TRADE_LOYALTY_2019_START_DATE = new Date('1/1/2019');
const TRADE_LOYALTY_2019_END_DATE = new Date('12/31/2019');

type ChildProgramListItemProps = {
    description?: ?Node,
    isNextTier?: boolean,
};

// export for testing
export function ChildProgramListItem({ description, isNextTier }: ChildProgramListItemProps) {
    if (!description) {
        return null;
    }

    const outerClass = classnames(styles.childProgramListItem, {
        [styles.nextTierListItem]: isNextTier,
    });

    const listIcon = isNextTier ? (
        <LockIcon className={classnames(styles.listIcon, styles.nextTierListIcon)} />
    ) : (
        <CheckmarkIcon className={classnames(styles.listIcon, styles.currentTierListIcon)} />
    );

    return (
        <div className={outerClass}>
            {listIcon}
            {description}
        </div>
    );
}

type NextStatusProps = {
    nextStatusName: ?string,
    nextStatusPrograms: $ReadOnlyArray<?{ +longCopy: ?string }>,
    scrollToBenefitsChart: (e?: SyntheticEvent<*>) => void,
};

// export for testing
export function NextStatus({
    nextStatusName,
    nextStatusPrograms,
    scrollToBenefitsChart,
}: NextStatusProps) {
    return (
        <React.Fragment>
            <BarHeader
                title={
                    <FormattedMessage
                        id="abt.rewards.NextStatusComparison.nextTierHeader"
                        defaultMessage="Level Up to {nextStatusName} in 2019"
                        values={{ nextStatusName }}
                    />
                }
                htmlElementType="h2"
            />
            <div className={`${styles.content} ${styles.nextTierContent}`}>
                {nextStatusPrograms.filter(Boolean).map(({ longCopy }) => (
                    <ChildProgramListItem description={longCopy} isNextTier key={longCopy} />
                ))}
                <a onClick={scrollToBenefitsChart} data-tn="view-next-benefits">
                    <FormattedMessage
                        id="abt.rewards.NextStatusComparison.viewNextBenefits"
                        defaultMessage="View All {nextStatusName} Benefits"
                        values={{ nextStatusName }}
                    />
                </a>
            </div>
        </React.Fragment>
    );
}

type WhatsNextPlatinumProps = {
    tradeRepFullName: ?string,
    tradeRepEmail: ?string,
};

// export for testing
export function WhatsNextPlatinum({ tradeRepFullName, tradeRepEmail }: WhatsNextPlatinumProps) {
    return (
        <React.Fragment>
            <BarHeader
                title={
                    <FormattedMessage
                        id="abt.rewards.NextStatusComparison.benefitEvenMore"
                        defaultMessage="Benefit Even More"
                    />
                }
                htmlElementType="h2"
            />
            <div className={`${styles.content} ${styles.nextTierContent}`}>
                <ChildProgramListItem
                    description={
                        <FormattedMessage
                            id="abt.rewards.NextStatusComparison.theresMorePlatinum"
                            defaultMessage="There's more in store for you at the Platinum tier, so keep earning points and unlock exclusive rewards, experiences and services, tailored to your firm. Talk to your Trade representative to learn more."
                        />
                    }
                    isNextTier
                />
                <div className={styles.tradeRepInfo}>
                    <div className={styles.tradeRepLabel}>
                        <FormattedMessage
                            id="abt.rewards.NextStatusComparison.yourTradeRep"
                            defaultMessage="your trade representative"
                        />
                    </div>
                    <div>{tradeRepFullName}</div>
                    <div>{tradeRepEmail}</div>
                </div>
            </div>
        </React.Fragment>
    );
}

type Props = {
    scrollToBenefitsChart: (e?: SyntheticEvent<*>) => void,
    tradeFirm: TradeFirm,
    viewer: Viewer,
};

export function NextStatusComparisonComponent(props: Props) {
    const { scrollToBenefitsChart, tradeFirm, viewer } = props;

    const tradeRepData = getter(viewer)
        .at('tradeRep')
        .at(0)
        .at('userDataB')
        .value(0);
    const tradeRepEmail = getter(tradeRepData).value('email');
    const tradeRepFullName = getter(tradeRepData).value('displayName');

    const rewardsGetter = getter(tradeFirm).at('rewards');
    const currentStatusCode =
        rewardsGetter
            .at('currentProgram')
            .at('programInfo')
            .value('code') || /* istanbul ignore next */ '';
    const currentStatusName = rewardsGetter
        .at('currentProgram')
        .at('programInfo')
        .value('shortCopy');
    const currentStatusPrograms =
        rewardsGetter
            .at('rewardsPageComparisonHeader')
            .at('currentProgram')
            .value('children') || /* istanbul ignore next */ [];
    const currentStatusIconUri = rewardsGetter
        .at('currentProgram')
        .at('programInfo')
        .value('iconUri');
    const nextStatusName = rewardsGetter
        .at('nextProgram')
        .at('programInfo')
        .value('shortCopy');
    const nextStatusPrograms =
        rewardsGetter
            .at('rewardsPageComparisonHeader')
            .at('nextProgram')
            .value('children') || /* istanbul ignore next */ [];

    const diamondImage = (
        <div className={styles.diamondImageContainer}>
            <div className={styles.diamondImageWrapper}>
                <img className={styles.diamondImage} src={currentStatusIconUri} />
            </div>
        </div>
    );

    return (
        <div className={styles.container}>
            <div
                className={`${styles.childProgramModule} ${styles.currentProgramModule}`}
                data-tn="current-status-module"
            >
                <div className={styles.currentProgramHeader}>
                    <FormattedMessage
                        id="abt.rewards.NextStatusComparison.currentProgramHeader"
                        defaultMessage="Current Status: {currentProgramLabel}"
                        values={{
                            currentProgramLabel: (
                                <React.Fragment>
                                    {diamondImage}
                                    <span
                                        className={classnames(styles.currentStatusName, {
                                            [styles.colorMember]:
                                                currentStatusCode === TPN_TIER_MEMBER,
                                            [styles.colorBronze]:
                                                currentStatusCode === TPN_TIER_BRONZE,
                                            [styles.colorSilver]:
                                                currentStatusCode === TPN_TIER_SILVER,
                                            [styles.colorGold]: currentStatusCode === TPN_TIER_GOLD,
                                            [styles.colorPlatinum]:
                                                currentStatusCode === TPN_TIER_PLATINUM,
                                        })}
                                    >
                                        {currentStatusName}
                                    </span>
                                </React.Fragment>
                            ),
                        }}
                    />
                </div>
                <div className={styles.currentStatusDateRange}>
                    <FormattedMessage
                        id="abt.rewards.NextStatusComparison.currentStatusDateRange"
                        defaultMessage="Valid {startDate} - {endDate}"
                        values={{
                            startDate: (
                                <FormattedDate
                                    value={TRADE_LOYALTY_2019_START_DATE}
                                    year="numeric"
                                    month="long"
                                    day="numeric"
                                />
                            ),
                            endDate: (
                                <FormattedDate
                                    value={TRADE_LOYALTY_2019_END_DATE}
                                    year="numeric"
                                    month="long"
                                    day="numeric"
                                />
                            ),
                        }}
                    />
                </div>
                <div className={styles.content}>
                    {currentStatusPrograms.filter(Boolean).map(({ longCopy }) => (
                        <ChildProgramListItem description={longCopy} key={longCopy} />
                    ))}
                    <a onClick={scrollToBenefitsChart} data-tn="view-current-benefits">
                        <FormattedMessage
                            id="abt.rewards.NextStatusComparison.viewCurrentBenefits"
                            defaultMessage="View All {currentStatusName} Benefits"
                            values={{ currentStatusName }}
                        />
                    </a>
                </div>
            </div>
            {currentStatusCode === TPN_TIER_PLATINUM ? (
                <div className={styles.childProgramModule} data-tn="next-status-module-platinum">
                    <WhatsNextPlatinum
                        tradeRepEmail={tradeRepEmail}
                        tradeRepFullName={tradeRepFullName}
                    />
                </div>
            ) : (
                <div className={styles.childProgramModule} data-tn="next-status-module">
                    <NextStatus
                        nextStatusName={nextStatusName}
                        nextStatusPrograms={nextStatusPrograms}
                        scrollToBenefitsChart={scrollToBenefitsChart}
                    />
                </div>
            )}
            <ProfileShowcase tradeFirm={(tradeFirm: Object)} viewer={(viewer: Object)} />
        </div>
    );
}

export const NextStatusComparison = createFragmentContainer(NextStatusComparisonComponent, {
    tradeFirm: graphql`
        fragment NextStatusComparison_tradeFirm on TradeFirmType {
            ...ProfileShowcase_tradeFirm
            rewards {
                currentProgram {
                    programInfo {
                        code
                        iconUri
                        shortCopy
                    }
                }
                nextProgram {
                    programInfo {
                        code
                        shortCopy
                    }
                }
                rewardsPageComparisonHeader {
                    currentProgram {
                        children {
                            longCopy
                        }
                    }
                    nextProgram {
                        children {
                            longCopy
                        }
                    }
                }
            }
        }
    `,
    viewer: graphql`
        fragment NextStatusComparison_viewer on Viewer {
            ...ProfileShowcase_viewer
            tradeRep: userRelationships(userIds: $userIds, relationshipType: TRADE)
                @include(if: $hasUserId) {
                userDataB {
                    ... on TradeRepData {
                        displayName
                        email
                    }
                }
            }
        }
    `,
});
